// The area illustrations are avatar-style FULL-BLEED SQUARE ICONS for the
// sidebar area nav — each portrait carries a small role indicator (compass /
// raised alert / star badge / ADMIN cap + key) that is the area's identity at a
// glance. Shipped as 128px squares (downscaled from the square sources with
// Lanczos, then losslessly recompressed) so they stay crisp at the 56px render
// size — 128px covers 2x-DPI displays (needs 112px) — without paying ~100KB per
// image. ES-imported so Vite hashes them into the bundle.
//
// Sources must be FULL-BLEED squares. Several of the later portraits arrived
// with their rounded corners baked in as opaque BLACK pixels, which read as dark
// wedges against the small CSS radius here; those were flood-filled with the
// art's own background green before downscaling. A portrait added with black
// corners intact will look broken on the light theme. SAMPLE that green from the
// source rather than reusing a neighbour's — it varies per portrait.
//
// The art is DECORATIVE: alt="" + role="presentation" — the nav label (or the
// link's `title` in rail mode) is the accessible name. Non-area nav items keep
// the line icon set (icons.tsx).
import type { Area } from "../lib/areas";

import abTestsArt from "../assets/area-ab-tests.png";
import alertsArt from "../assets/area-alerts.png";
import developersArt from "../assets/area-developers.png";
import explorerArt from "../assets/area-explorer.png";
import funnelArt from "../assets/area-funnel.png";
import launchDayArt from "../assets/launch-day.png";
import systemAdminArt from "../assets/area-system-admin.png";
import talentManagementArt from "../assets/area-talent-management.png";
import versionCohortsArt from "../assets/area-version-cohorts.png";
import { AREA_ICON } from "./icons";

/** Portrait art for the areas that HAVE a page. The rest fall back to the line
 *  icon set (`AreaArtIcon` below).
 *
 *  All NINE navigable areas — every `AREA_META` entry with a non-null `path` —
 *  now carry purpose-drawn art. The Explorer used to borrow the customer-support
 *  portrait (that art was drawn for the people-and-conversations screen, and the
 *  Explorer became that screen); it has its own compass portrait now, so that
 *  borrowed art was deleted.
 *
 *  That makes `AreaArtIcon`'s line-icon fallback unreachable for NAV purposes:
 *  every area the sidebar renders has an entry here. It is kept deliberately, as
 *  the guard for the next area added without art — the alternative is a nav row
 *  with a hole in it on the release that adds the area and before the one that
 *  draws its portrait. Do not delete it just because nothing reaches it today.
 *  (The home cards read `AREA_ICON` directly, so the line icons are still the
 *  home-card glyphs regardless.)
 *
 *  `system_admin` HAS a portrait but is still not a peer of the product areas:
 *  it administers the console rather than the product, and the separation now
 *  lives entirely in the nav row's `--meta` treatment (a rule above it, in
 *  app-shell.tsx + globals.css) rather than in withholding the art. Its ADMIN
 *  cap + key is the role indicator for exactly that authority. Do not collapse
 *  the divider just because the row looks like the others now.
 *
 *  `customer_support` and `user_administration` have no entry because they are
 *  capability-only — no page, never in the nav, so a portrait for them could
 *  never render. */
export const AREA_ART: Partial<Record<Area, string>> = {
  explorer: explorerArt,
  alerts: alertsArt,
  talent_management: talentManagementArt,
  // Mira with a loupe over a bug — the debugging tab. Its source arrived with the
  // baked-in black rounded corners described above; those were flood-filled with
  // the art's own background green before the 128px downscale.
  developers: developersArt,
  // Mira with the rising chart — carried over from when `/launch` was a
  // hardcoded nav row outside the area list (migration 0114 made it a real
  // area with a real grant, so it moved into this map like every other area;
  // leaving the old hardcoded row in app-shell.tsx alongside it rendered
  // Launch Day TWICE in the sidebar for anyone holding the grant).
  launch_day: launchDayArt,
  // Mira giving the OK sign beside a funnel glyph — the Funnel section. Its
  // source arrived with baked-in rounded corners; those were flattened onto
  // the art's own background green before the 128px downscale.
  funnel: funnelArt,
  system_admin: systemAdminArt,
  // Version Cohorts — Mira beside three groups of people, the cohorts the page
  // compares. Its source arrived with the baked-in black corners described above;
  // those were flood-filled with the art's own background green (#033a2e, sampled
  // from the source) before the 128px downscale.
  version_cohorts: versionCohortsArt,
  // Playground — Mira giving a thumbs-up beside a swing set, the experiment
  // arms the A/B page compares. Its source arrived with the baked-in black
  // corners described above; those were flood-filled with the art's own
  // background green (#013d2b, sampled from the source) before the 128px
  // downscale.
  ab_tests: abTestsArt,
};

/** Full-bleed rounded-square area thumbnail for the sidebar nav, or the line
 *  icon for a meta area with no portrait. */
export function AreaArtIcon({ area }: { area: Area }) {
  const art = AREA_ART[area];
  if (!art) {
    // Falls back to the shared AREA_ICON map rather than a hardcoded icon, so
    // an area's nav glyph and its home-card glyph can never disagree.
    const Icon = AREA_ICON[area];
    return (
      <span className="ad-nav__icon">
        <Icon />
      </span>
    );
  }
  return <img className="ad-nav__art" src={art} alt="" role="presentation" />;
}
